import { Box, Button, Flex, Typography } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import styled from "styled-components"

import { getUrlProps } from "helpers/landing"
import { cn } from "styles/utils"

import Image from "../image"

const ItemContainer = styled(Flex).attrs((props) => ({
  bg: "blackPrint",
  bottom: 0,
  color: "white",
  flexDirection: "column",
  height: "auto",
  justifyContent: "flex-end",
  left: 0,
  position: "absolute",
  px: "12px",
  py: { _: 1, desktop: "12px" },
  width: "100%",
  ...props,
}))``

const Title = styled(Typography).attrs((props) => ({
  textTransform: "uppercase",
  ...props,
}))``

const Subtitle = styled(Typography).attrs((props) => ({
  display: { _: "none", tablet: "block" },
  ...props,
}))``

const CTABox = styled(Box).attrs((props) => ({
  fontSize: { _: "15px", tablet: "17px", widescreen: "19px" },
  fontWeight: { _: "600", widescreen: "400" },
  lineHeight: "22px",
  ...props,
}))``

const CTA = styled(Button).attrs({
  variant: "text",
})`
  text-decoration: underline;
`

const ImageLink = styled(Button).attrs({
  variant: "text",
})`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 2;

  &:focus ~ ${ItemContainer}, &:hover ~ ${ItemContainer} {
    ${CTA} {
      text-decoration: none;
    }
  }
`

const FeatureGridItem = ({ element, isMenu, item, lazyload, onItemClick }) => {
  const urlProps = getUrlProps(
    item.url,
    element.anchor_name || element.id,
    onItemClick
  )

  if (isMenu) {
    const { className: titleClassName, ...titleProps } =
      item.style?.title_style || {}
    const { className: ctaClassName, ...ctaProps } = item.style?.cta_style || {}

    return (
      <div>
        <Box mb="4px" position="relative" {...item.style?.block_style}>
          {item.url && (
            <ImageLink
              aria-label={item.image?.alt || item.title}
              {...urlProps}
            />
          )}
          <Image image={item.image} lazyload={lazyload} />
        </Box>
        {item.title && (
          <h2
            className={cn("body font-semibold", titleClassName)}
            dangerouslySetInnerHTML={{ __html: item.title }}
            {...titleProps}
          />
        )}
        {item.subtitle && (
          <Subtitle
            {...item.style?.subtitle_style}
            dangerouslySetInnerHTML={{ __html: item.subtitle }}
            variant="body"
          />
        )}
        {item.cta && item.url && (
          <div className={cn("body-sm", ctaClassName)} {...ctaProps}>
            <CTA aria-hidden tabIndex="-1" {...urlProps}>
              {item.cta}
            </CTA>
          </div>
        )}
      </div>
    )
  }

  return (
    <Box position="relative" {...item.style?.block_style}>
      {item.url && (
        <ImageLink aria-label={item.image?.alt || item.title} {...urlProps} />
      )}
      <Image image={item.image} lazyload={lazyload} />
      {(item.title || item.subtitle || (item.cta && item.url)) && (
        <ItemContainer {...item.style?.container_style}>
          {item.title && (
            <Title
              as="h2"
              {...item.style?.title_style}
              dangerouslySetInnerHTML={{ __html: item.title }}
              variant="h3"
            />
          )}
          {item.subtitle && (
            <Subtitle
              {...item.style?.subtitle_style}
              dangerouslySetInnerHTML={{ __html: item.subtitle }}
              variant="body"
            />
          )}
          {item.cta && item.url && (
            <CTABox {...item.style?.cta_style}>
              <CTA aria-hidden tabIndex="-1" {...urlProps}>
                {item.cta}
              </CTA>
            </CTABox>
          )}
        </ItemContainer>
      )}
    </Box>
  )
}

FeatureGridItem.propTypes = {
  element: PropTypes.object,
  isMenu: PropTypes.bool,
  item: PropTypes.object,
  lazyload: PropTypes.bool,
  onItemClick: PropTypes.func,
}

export default FeatureGridItem
